if (!customElements.get('fragrance-collection-toolbar')) {
  class FragranceCollectionToolbar extends HTMLElement {
    constructor() {
      super();

      this.raf = null;
      this.resizeTimer = null;
      this.lastFocusedElement = null;

      this.stickyTriggerY = 0;
      this.isStickyCloneVisible = false;
      this.isSyncingNavigation = false;
      this.dragStates = new WeakMap();

      this.mobileGridColumns = 2;
      this.gridObserver = null;
      this.gridApplyTimer = null;

      this.facetResultObserver = null;
      this.toolbarRefreshTimer = null;
      this.toolbarRefreshController = null;

      this.pageTitleHeading = null;
      this.pageTitleSlot = null;
      this.pageTitleOriginalParent = null;
      this.pageTitleOriginalNextSibling = null;
      this.pageTitleSourceSection = null;
      this.pageTitleSourceCollapsed = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentPointerDown =
        this.handleDocumentPointerDown.bind(this);
    }

    connectedCallback() {
      this.stickyShell = this.querySelector('[data-fct-sticky-shell]');
      this.nav = this.querySelector('[data-fct-nav]');
      this.mountPageTitle();
      this.bindOriginalControls();
      this.bindNavigationControls(this);
      this.enableDragScroll(this.nav);
      this.createStickyClone();
      this.initMobileGridControls();
      this.initNativeFacetSync();

      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener(
        'pointerdown',
        this.handleDocumentPointerDown
      );
      window.addEventListener('scroll', this.handleScroll, {
        passive: true,
      });
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('load', this.handleWindowLoad);

      window.requestAnimationFrame(() => {
        this.measureStickyTrigger();
        this.updateStickyClone();
        this.centerActiveCollection(this.nav);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener(
        'pointerdown',
        this.handleDocumentPointerDown
      );
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('load', this.handleWindowLoad);

      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
      }

      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }

      if (this.gridApplyTimer) {
        window.clearTimeout(this.gridApplyTimer);
      }

      this.gridObserver?.disconnect();
      this.facetResultObserver?.disconnect();

      if (this.toolbarRefreshTimer) {
        window.clearTimeout(this.toolbarRefreshTimer);
      }

      this.toolbarRefreshController?.abort();

      this.restorePageTitle();

      this.stickyClone?.remove();
      this.stickyClone = null;
    }

    mountPageTitle() {
      if (!this.stickyShell || this.pageTitleSlot) return;

      const navWrap = this.stickyShell.querySelector('.fct__nav-wrap');
      const toolbar = this.stickyShell.querySelector('.fct__toolbar');
      const heading = this.findPageTitleHeading();

      if (!navWrap || !toolbar || !heading) return;

      this.pageTitleHeading = heading;
      this.pageTitleOriginalParent = heading.parentNode;
      this.pageTitleOriginalNextSibling = heading.nextSibling;
      this.pageTitleSourceSection = heading.closest('.shopify-section');

      const slot = document.createElement('div');
      slot.className = 'fct__page-title';
      slot.setAttribute('data-fct-page-title', '');

      heading.classList.add('fct__page-title-heading');
      slot.appendChild(heading);
      navWrap.insertAdjacentElement('afterend', slot);

      this.pageTitleSlot = slot;
      this.collapseEmptyTitleSource();
    }

    findPageTitleHeading() {
      const main =
        this.closest('main') ||
        document.querySelector('main') ||
        document;

      const headingTags = 'h1,h2,h3,h4,h5,h6';
      const specificSelectors = [
        `.collection-banner :is(${headingTags})`,
        `.collection-hero :is(${headingTags})`,
        `[class*="collection-banner"] :is(${headingTags})`,
        `[class*="collection-hero"] :is(${headingTags})`,
        `[id*="collection-banner"] :is(${headingTags})`,
        `[id*="collection-hero"] :is(${headingTags})`,
        `[data-section-type="collection-banner"] :is(${headingTags})`,
      ];

      const isBeforeToolbar = (heading) =>
        !this.contains(heading) &&
        Boolean(
          heading.compareDocumentPosition(this) &
            Node.DOCUMENT_POSITION_FOLLOWING
        );

      const specificHeadings = Array.from(
        main.querySelectorAll(specificSelectors.join(','))
      ).filter(isBeforeToolbar);

      if (specificHeadings.length) {
        return specificHeadings[specificHeadings.length - 1];
      }

      const h1Headings = Array.from(
        main.querySelectorAll('h1')
      ).filter(isBeforeToolbar);

      return h1Headings[h1Headings.length - 1] || null;
    }

    collapseEmptyTitleSource() {
      const sourceSection = this.pageTitleSourceSection;
      const currentSection = this.closest('.shopify-section');

      if (!sourceSection || sourceSection === currentSection) return;

      const copy = sourceSection.cloneNode(true);

      copy.querySelectorAll(
        'script, style, noscript, template'
      ).forEach((element) => element.remove());

      const hasRemainingText = Boolean(copy.textContent?.trim());
      const hasRemainingContent = Boolean(
        copy.querySelector(
          'img, picture, video, iframe, canvas, form, button, input, select, textarea, [role="img"]'
        )
      );

      if (hasRemainingText || hasRemainingContent) return;

      sourceSection.classList.add('fct-title-source--empty');
      this.pageTitleSourceCollapsed = true;
    }

    restorePageTitle() {
      const heading = this.pageTitleHeading;
      const parent = this.pageTitleOriginalParent;
      const nextSibling = this.pageTitleOriginalNextSibling;

      if (heading && parent?.isConnected) {
        if (nextSibling?.parentNode === parent) {
          parent.insertBefore(heading, nextSibling);
        } else {
          parent.appendChild(heading);
        }
      }

      heading?.classList.remove('fct__page-title-heading');

      if (this.pageTitleSourceCollapsed) {
        this.pageTitleSourceSection?.classList.remove(
          'fct-title-source--empty'
        );
      }

      this.pageTitleSlot?.remove();

      this.pageTitleHeading = null;
      this.pageTitleSlot = null;
      this.pageTitleOriginalParent = null;
      this.pageTitleOriginalNextSibling = null;
      this.pageTitleSourceSection = null;
      this.pageTitleSourceCollapsed = false;
    }

    bindOriginalControls() {
      this.bindNativeDrawerButtons(this);
      this.bindGridViewControls(this);
      this.bindSortControls(this);

      this.querySelectorAll('[data-fct-show-more]').forEach((button) => {
        button.addEventListener('click', () => {
          const values = button
            .closest('.fct__filter-content')
            ?.querySelector('[data-fct-filter-values]');

          if (!values) return;

          const isExpanded = values.classList.toggle('is-expanded');

          button.textContent = isExpanded
            ? button.dataset.lessLabel
            : button.dataset.moreLabel;
        });
      });
    }

    bindSortControls(root) {
      if (!root) return;

      root.querySelectorAll('[data-fct-sort-dropdown]').forEach(
        (dropdown) => {
          if (dropdown.__fctSortBound) return;

          const trigger = dropdown.querySelector(
            '[data-fct-sort-trigger]'
          );
          const menu = dropdown.querySelector('[data-fct-sort-menu]');
          const select = dropdown.querySelector('[data-fct-sort]');

          if (!trigger || !menu || !select) return;

          dropdown.__fctSortBound = true;

          const options = Array.from(
            menu.querySelectorAll('[data-fct-sort-option]')
          );

          const setExpanded = (expanded, focusOption = false) => {
            const shouldOpen = Boolean(expanded);

            if (shouldOpen) {
              this.closeSortDropdowns(dropdown);
            }

            dropdown.classList.toggle('is-open', shouldOpen);
            trigger.setAttribute(
              'aria-expanded',
              String(shouldOpen)
            );
            menu.hidden = !shouldOpen;

            if (shouldOpen && focusOption) {
              const selectedOption =
                menu.querySelector('.is-selected') || options[0];

              selectedOption?.focus();
            }
          };

          dropdown.__fctSetExpanded = setExpanded;

          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            setExpanded(menu.hidden);
          });

          trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
              return;
            }

            event.preventDefault();
            setExpanded(true, true);
          });

          menu.addEventListener('keydown', (event) => {
            const currentIndex = options.indexOf(document.activeElement);

            if (event.key === 'Escape') {
              event.preventDefault();
              setExpanded(false);
              trigger.focus();
              return;
            }

            if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();

              const target = event.key === 'Home'
                ? options[0]
                : options[options.length - 1];

              target?.focus();
              return;
            }

            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
              return;
            }

            event.preventDefault();

            const direction = event.key === 'ArrowDown' ? 1 : -1;
            const fallbackIndex = direction > 0 ? -1 : 0;
            const startIndex = currentIndex >= 0
              ? currentIndex
              : fallbackIndex;
            const nextIndex =
              (startIndex + direction + options.length) % options.length;

            options[nextIndex]?.focus();
          });

          options.forEach((option) => {
            option.addEventListener('click', () => {
              const value = option.dataset.value;

              if (!value) return;

              setExpanded(false);

              if (select.value === value) {
                trigger.focus();
                return;
              }

              select.value = value;

              options.forEach((item) => {
                const isSelected = item === option;

                item.classList.toggle('is-selected', isSelected);
                item.setAttribute(
                  'aria-selected',
                  String(isSelected)
                );
              });

              select.dispatchEvent(
                new Event('change', { bubbles: true })
              );
            });
          });

          select.addEventListener('change', (event) => {
            this.applySort(event.target.value);
          });
        }
      );
    }

    closeSortDropdowns(except = null) {
      this.querySelectorAll('[data-fct-sort-dropdown]').forEach(
        (dropdown) => {
          if (dropdown === except) return;

          dropdown.__fctSetExpanded?.(false);
        }
      );
    }

    handleDocumentPointerDown(event) {
      const clickedDropdown = event.target.closest?.(
        '[data-fct-sort-dropdown]'
      );

      if (clickedDropdown && this.contains(clickedDropdown)) {
        this.closeSortDropdowns(clickedDropdown);
        return;
      }

      this.closeSortDropdowns();
    }

    bindNativeDrawerButtons(root) {
      if (!root) return;

      root.querySelectorAll('[data-fct-native-drawer-open]').forEach(
        (button) => {
          /*
           * Do not use a data-* attribute as the "already bound" flag.
           * The sticky toolbar is created with cloneNode(true), so data-*
           * attributes are copied to the clone while event listeners are not.
           * That made the sticky FILTRI button look bound even though it had
           * no click listener.
           *
           * A JS property is not copied by cloneNode(), so both the original
           * button and the sticky-clone button receive their own listener.
           */
          if (button.__fctNativeDrawerBound) return;

          button.__fctNativeDrawerBound = true;

          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openNativeFacetDrawer(button);
          });
        }
      );
    }

    findNativeFacetTrigger() {
      const selectors = [
        '[data-fragrance-native-facets-source] .facet-drawer-button',
        '#ProductGridWrapper .facet-drawer-button',
        '.main-collection-product-grid .facet-drawer-button',
        '.collection__content .facet-drawer-button',
        '.collection .facet-drawer-button',
        'main .facet-drawer-button',
        'button[aria-controls="FacetDrawer"]',
        '[data-facet-loading][aria-controls*="FacetDrawer"]',
      ];

      for (const selector of selectors) {
        const candidates = document.querySelectorAll(selector);

        for (const candidate of candidates) {
          if (
            this.contains(candidate) ||
            candidate.closest('fragrance-collection-toolbar')
          ) {
            continue;
          }

          return candidate;
        }
      }

      return null;
    }

    openNativeFacetDrawer(trigger) {
      const nativeTrigger = this.findNativeFacetTrigger();

      if (!nativeTrigger) {
        console.warn(
          '[Fragrance toolbar] Native Product Grid facet trigger was not found.'
        );
        return;
      }

      this.lastFocusedElement =
        trigger || document.activeElement;

      nativeTrigger.click();
    }

    initNativeFacetSync() {
      const productGrid =
        document.getElementById('ProductGridContainer');

      if (!productGrid) return;

      this.facetResultObserver =
        new MutationObserver(() => {
          this.scheduleToolbarStateRefresh();
        });

      this.facetResultObserver.observe(productGrid, {
        childList: true,
        subtree: true,
      });
    }

    scheduleToolbarStateRefresh() {
      if (this.toolbarRefreshTimer) {
        window.clearTimeout(this.toolbarRefreshTimer);
      }

      this.toolbarRefreshTimer =
        window.setTimeout(() => {
          this.refreshToolbarStateFromServer();
          this.toolbarRefreshTimer = null;
        }, 140);
    }

    async refreshToolbarStateFromServer() {
      const sectionId = this.dataset.sectionId;

      if (!sectionId) return;

      this.toolbarRefreshController?.abort();
      this.toolbarRefreshController =
        new AbortController();

      const url =
        new URL(window.location.href);

      url.searchParams.set(
        'section_id',
        sectionId
      );

      try {
        const response = await fetch(
          url.toString(),
          {
            signal:
              this.toolbarRefreshController.signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );

        if (!response.ok) return;

        const responseText =
          await response.text();

        const parsedHTML =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        const sourceToolbar =
          parsedHTML.querySelector(
            `#FragranceCollectionToolbar-${sectionId}`
          ) ||
          parsedHTML.querySelector(
            'fragrance-collection-toolbar'
          );

        if (!sourceToolbar) return;

        this.syncToolbarStateFromSource(
          sourceToolbar
        );
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[Fragrance toolbar] Could not refresh toolbar state.',
            error
          );
        }
      }
    }

    syncToolbarStateFromSource(sourceToolbar) {
      const sourceProductCount =
        sourceToolbar.querySelector(
          '[data-fct-product-count]'
        );

      const sourceFilterCount =
        sourceToolbar.querySelector(
          '[data-fct-filter-count]'
        );

      const sourceDynamicFilters =
        sourceToolbar.querySelector(
          '[data-fct-dynamic-filters]'
        );

      if (sourceProductCount) {
        this.querySelectorAll(
          '[data-fct-product-count]'
        ).forEach((target) => {
          target.innerHTML =
            sourceProductCount.innerHTML;
        });
      }

      if (sourceFilterCount) {
        this.querySelectorAll(
          '[data-fct-filter-count]'
        ).forEach((target) => {
          target.textContent =
            sourceFilterCount.textContent;
        });
      }

      if (sourceDynamicFilters) {
        this.querySelectorAll(
          '[data-fct-dynamic-filters]'
        ).forEach((target) => {
          target.innerHTML =
            sourceDynamicFilters.innerHTML;
        });
      }

      this.syncGridViewButtons();
      this.updateCloneGeometry();
      this.updateStickyClone();
    }

    createStickyClone() {
      if (!this.stickyShell || this.stickyClone) return;

      this.stickyClone = this.stickyShell.cloneNode(true);
      this.stickyClone.classList.add('fct__sticky-clone');
      this.stickyClone.removeAttribute('data-fct-sticky-shell');
      this.stickyClone.setAttribute('aria-hidden', 'true');
      this.stickyClone.inert = true;

      /*
       * Prevent duplicate HTML ids from the cloned toolbar.
       * aria-controls is kept because it should still open the
       * original filter drawer.
       */
      this.stickyClone.querySelectorAll('[id]').forEach((element) => {
        element.removeAttribute('id');
      });

      /*
       * Remove the old DOM marker if it exists from a previous toolbar
       * version. cloneNode(true) copies attributes but not listeners.
       */
      this.stickyClone.querySelectorAll(
        '[data-fct-native-drawer-bound]'
      ).forEach((element) => {
        element.removeAttribute('data-fct-native-drawer-bound');
      });

      this.appendChild(this.stickyClone);

      this.stickyCloneNav =
        this.stickyClone.querySelector('[data-fct-nav]');

      this.enableDragScroll(this.stickyCloneNav);
      this.bindNavigationControls(this.stickyClone);

      this.bindNativeDrawerButtons(this.stickyClone);

      this.bindGridViewControls(this.stickyClone);
      this.bindSortControls(this.stickyClone);

      this.updateCloneGeometry();
    }

    bindNavigationControls(root) {
      if (!root) return;
      if (!window.matchMedia('(min-width: 768px)').matches) return;

      const nav = root.querySelector('[data-fct-nav]');
      const previousButton = root.querySelector('[data-fct-nav-prev]');
      const nextButton = root.querySelector('[data-fct-nav-next]');

      if (!nav || !previousButton || !nextButton) return;
      if (previousButton.__fctNavBound || nextButton.__fctNavBound) return;

      previousButton.__fctNavBound = true;
      nextButton.__fctNavBound = true;

      const updateButtonState = () => {
        const maxScrollLeft = Math.max(
          0,
          nav.scrollWidth - nav.clientWidth
        );
        const tolerance = 2;
        const canScroll = maxScrollLeft > tolerance;

        previousButton.disabled =
          !canScroll || nav.scrollLeft <= tolerance;

        nextButton.disabled =
          !canScroll ||
          nav.scrollLeft >= maxScrollLeft - tolerance;
      };

      previousButton.addEventListener('click', () => {
        this.scrollNavigation(-1, nav);
      });

      nextButton.addEventListener('click', () => {
        this.scrollNavigation(1, nav);
      });

      nav.addEventListener('scroll', updateButtonState, {
        passive: true,
      });

      window.requestAnimationFrame(updateButtonState);
    }

    bindGridViewControls(root) {
      if (!root) return;

      root.querySelectorAll('[data-fct-grid-view]').forEach(
        (button) => {
          if (button.dataset.fctGridBound === 'true') return;

          button.dataset.fctGridBound = 'true';

          button.addEventListener('click', () => {
            const columns =
              Number(button.dataset.fctGridView) === 1
                ? 1
                : 2;

            this.applyMobileGridColumns(columns, {
              persist: true,
            });
          });
        }
      );
    }

    initMobileGridControls() {
      this.mobileGridColumns =
        this.getCurrentMobileGridColumns();

      this.applyMobileGridColumns(
        this.mobileGridColumns,
        { persist: false }
      );

      const observerRoot =
        document.querySelector('main') ||
        document.body;

      this.gridObserver = new MutationObserver(() => {
        if (this.gridApplyTimer) {
          window.clearTimeout(this.gridApplyTimer);
        }

        this.gridApplyTimer = window.setTimeout(() => {
          this.applyMobileGridColumns(
            this.mobileGridColumns,
            { persist: false }
          );

          this.gridApplyTimer = null;
        }, 80);
      });

      this.gridObserver.observe(observerRoot, {
        childList: true,
        subtree: true,
      });
    }

    getNativeMobileSwitcher() {
      return document.querySelector(
        [
          'collection-layout-switch[device="mobile"]',
          'grid-layout-switch[device="mobile"]',
          '[data-mobile-layout-switch]',
        ].join(',')
      );
    }

    getProductGridTarget() {
      const nativeSwitcher =
        this.getNativeMobileSwitcher();

      const controlledId =
        nativeSwitcher?.getAttribute('aria-controls');

      if (controlledId) {
        const controlledGrid =
          document.getElementById(controlledId);

        if (controlledGrid) {
          return controlledGrid;
        }
      }

      /*
       * Sleek renders the collection products with:
       *
       * <grid-list id="ProductsList"
       *   class="grid-list products-list ... f-grid-2-cols">
       */
      return document.querySelector(
        [
          '#ProductsList',
          'grid-list.products-list',
          'grid-list.grid-list',
          '.products-list.grid-list',
          '[data-product-grid]',
          '.collection-section .product-grid',
          '.main-collection-grid .product-grid',
          '#ResultsList .product-grid',
          'product-list[collection-mobile-layout]',
        ].join(',')
      );
    }

    getCurrentMobileGridColumns() {
      const grid = this.getProductGridTarget();

      if (grid?.classList.contains('f-grid-1-cols')) {
        return 1;
      }

      if (grid?.classList.contains('f-grid-2-cols')) {
        return 2;
      }

      const nativeLayout = grid?.getAttribute(
        'collection-mobile-layout'
      );

      if (nativeLayout === 'large') {
        return 1;
      }

      if (nativeLayout === 'medium') {
        return 2;
      }

      let stored = 2;

      try {
        stored = Number(
          window.localStorage.getItem(
            'fct-mobile-grid-columns'
          )
        );
      } catch (error) {
        // Storage may be blocked.
      }

      return stored === 1 ? 1 : 2;
    }

    applyMobileGridColumns(
      columns,
      { persist = false } = {}
    ) {
      const resolvedColumns =
        Number(columns) === 1 ? 1 : 2;

      const layout =
        resolvedColumns === 1
          ? 'large'
          : 'medium';

      this.mobileGridColumns = resolvedColumns;

      const grid = this.getProductGridTarget();

      if (grid) {
        grid.setAttribute(
          'collection-mobile-layout',
          layout
        );

        grid.setAttribute(
          'data-fct-mobile-columns',
          String(resolvedColumns)
        );

        /*
         * Sleek's native mobile column classes.
         * Tablet and desktop classes are intentionally untouched.
         */
        grid.classList.remove(
          'f-grid-1-cols',
          'f-grid-2-cols'
        );

        grid.classList.add(
          resolvedColumns === 1
            ? 'f-grid-1-cols'
            : 'f-grid-2-cols'
        );

        grid.classList.toggle(
          'fct-mobile-grid--one',
          resolvedColumns === 1
        );

        grid.classList.toggle(
          'fct-mobile-grid--two',
          resolvedColumns === 2
        );

        grid.dispatchEvent(
          new CustomEvent('fct:mobile-grid-change', {
            bubbles: true,
            detail: {
              columns: resolvedColumns,
              layout,
            },
          })
        );
      }

      document.documentElement.setAttribute(
        'data-fct-mobile-grid',
        String(resolvedColumns)
      );

      this.syncGridViewButtons();

      if (!persist) return;

      try {
        window.localStorage.setItem(
          'fct-mobile-grid-columns',
          String(resolvedColumns)
        );
      } catch (error) {
        // Storage can be blocked in private browsing.
      }

      this.persistNativeMobileLayout(layout);

      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }

    syncGridViewButtons() {
      this.querySelectorAll(
        '[data-fct-grid-view]'
      ).forEach((button) => {
        const isActive =
          Number(button.dataset.fctGridView) ===
          this.mobileGridColumns;

        button.classList.toggle(
          'is-active',
          isActive
        );

        button.setAttribute(
          'aria-pressed',
          String(isActive)
        );
      });
    }

    async persistNativeMobileLayout(layout) {
      const root =
        window.Shopify?.routes?.root || '/';

      try {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              products_mobile_grid_mode: layout,
            },
          }),
        });
      } catch (error) {
        /*
         * The visual layout has already been applied. A failed cart
         * preference request should not break the grid control.
         */
      }
    }

    positionFragranceGroup() {
      const drawerScroll = this.querySelector('.fct__drawer-scroll');
      const fragranceGroup = drawerScroll?.querySelector(
        '.fct__filter-group--fragrance'
      );
      const firstNativeGroup = drawerScroll?.querySelector(
        '.fct__filter-group:not(.fct__filter-group--fragrance)'
      );

      if (fragranceGroup && firstNativeGroup) {
        firstNativeGroup.insertAdjacentElement(
          'afterend',
          fragranceGroup
        );
      }
    }

    getStickyOffset() {
      const styles = window.getComputedStyle(this);

      const property =
        window.matchMedia('(max-width: 767.98px)').matches
          ? '--fct-sticky-top-mobile'
          : '--fct-sticky-top-desktop';

      return parseFloat(styles.getPropertyValue(property)) || 0;
    }

    handleScroll() {
      if (this.raf) return;

      this.raf = window.requestAnimationFrame(() => {
        this.updateStickyClone();
        this.raf = null;
      });
    }

    handleResize() {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = window.setTimeout(() => {
        this.bindNavigationControls(this);
        this.bindNavigationControls(this.stickyClone);
        this.measureStickyTrigger();
        this.updateCloneGeometry();
        this.updateStickyClone();
        this.resizeTimer = null;
      }, 120);
    }

    handleWindowLoad() {
      this.measureStickyTrigger();
      this.updateCloneGeometry();
      this.updateStickyClone();
    }

    measureStickyTrigger() {
      if (!this.stickyShell) return;

      const stickyOffset = this.getStickyOffset();
      const shellRect = this.stickyShell.getBoundingClientRect();
      const originalTop = window.scrollY + shellRect.top;
      const fullContentHeight = this.stickyShell.offsetHeight;

      /*
       * Show the compact toolbar only after half of the complete
       * original toolbar has passed the configured sticky position.
       */
      this.stickyTriggerY =
        originalTop +
        fullContentHeight * 0.5 -
        stickyOffset;
    }

    updateStickyClone() {
      if (!this.stickyClone || !this.stickyShell) return;

      const releaseBuffer = 16;
      const shouldShow = this.isStickyCloneVisible
        ? window.scrollY >= this.stickyTriggerY - releaseBuffer
        : window.scrollY >= this.stickyTriggerY;

      if (shouldShow !== this.isStickyCloneVisible) {
        this.setStickyCloneVisible(shouldShow);
      }

      if (shouldShow) {
        this.updateCloneGeometry();
        this.updateFooterShift();
      }
    }

    setStickyCloneVisible(visible) {
      this.isStickyCloneVisible = visible;

      if (visible) {
        if (this.stickyCloneNav && this.nav) {
          this.stickyCloneNav.scrollLeft = this.nav.scrollLeft;
        }

        this.stickyClone.inert = false;
        this.stickyClone.setAttribute('aria-hidden', 'false');

        window.requestAnimationFrame(() => {
          this.classList.add('has-sticky-clone');
          this.stickyClone.classList.add('is-visible');
        });
      } else {
        if (this.stickyCloneNav && this.nav) {
          this.nav.scrollLeft = this.stickyCloneNav.scrollLeft;
        }

        this.classList.remove('has-sticky-clone');
        this.stickyClone.classList.remove('is-visible');
        this.stickyClone.setAttribute('aria-hidden', 'true');
        this.stickyClone.inert = true;
        this.style.removeProperty('--fct-clone-footer-shift');
      }
    }

    updateCloneGeometry() {
      if (!this.stickyClone) return;

      const sectionRect = this.getBoundingClientRect();

      this.style.setProperty(
        '--fct-clone-left',
        `${sectionRect.left}px`
      );

      this.style.setProperty(
        '--fct-clone-width',
        `${sectionRect.width}px`
      );
    }

    updateFooterShift() {
      if (!this.stickyClone) return;

      const stickyOffset = this.getStickyOffset();
      const cloneHeight = this.stickyClone.offsetHeight;
      const footer =
        document.querySelector(
          '.shopify-section-group-footer-group'
        ) || document.querySelector('footer');

      let footerShift = 0;

      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        const cloneBottom = stickyOffset + cloneHeight;

        if (footerTop < cloneBottom) {
          footerShift = footerTop - cloneBottom;
        }
      }

      this.style.setProperty(
        '--fct-clone-footer-shift',
        `${footerShift}px`
      );
    }

    enableDragScroll(nav) {
      if (!nav || this.dragStates.has(nav)) return;

      const state = {
        isPointerDown: false,
        isDragging: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        lastX: 0,
        lastTime: 0,
        velocity: 0,
        momentumFrame: null,
        suppressClickUntil: 0,
      };

      this.dragStates.set(nav, state);
      nav.classList.add('is-draggable');

      const getMirrorNavigation = () =>
        nav === this.nav ? this.stickyCloneNav : this.nav;

      const maxScrollLeft = () =>
        Math.max(0, nav.scrollWidth - nav.clientWidth);

      const clampScrollLeft = (value) =>
        Math.min(maxScrollLeft(), Math.max(0, value));

      const syncMirrorNavigation = () => {
        const mirror = getMirrorNavigation();

        if (!mirror) return;

        if (Math.abs(mirror.scrollLeft - nav.scrollLeft) > 0.5) {
          mirror.scrollLeft = nav.scrollLeft;
        }
      };

      const setScrollLeft = (value) => {
        const next = clampScrollLeft(value);

        nav.scrollLeft = next;
        syncMirrorNavigation();

        return next;
      };

      const stopMomentum = () => {
        if (state.momentumFrame) {
          window.cancelAnimationFrame(state.momentumFrame);
          state.momentumFrame = null;
        }

        nav.classList.remove('is-gliding');
      };

      const startMomentum = () => {
        stopMomentum();

        let velocity = Math.max(-2.5, Math.min(2.5, state.velocity));

        if (Math.abs(velocity) < 0.03) {
          state.velocity = 0;
          syncMirrorNavigation();
          return;
        }

        nav.classList.add('is-gliding');

        let previousTime = performance.now();

        const animate = (currentTime) => {
          const elapsed = Math.min(32, currentTime - previousTime);
          previousTime = currentTime;

          const currentScroll = nav.scrollLeft;
          const nextScroll = setScrollLeft(
            currentScroll + velocity * elapsed
          );

          velocity *= Math.pow(0.92, elapsed / 16.667);

          const reachedStart =
            nextScroll <= 0 && velocity < 0;
          const reachedEnd =
            nextScroll >= maxScrollLeft() && velocity > 0;

          if (
            Math.abs(velocity) < 0.02 ||
            reachedStart ||
            reachedEnd
          ) {
            state.velocity = 0;
            state.momentumFrame = null;
            nav.classList.remove('is-gliding');
            syncMirrorNavigation();
            return;
          }

          state.momentumFrame =
            window.requestAnimationFrame(animate);
        };

        state.momentumFrame =
          window.requestAnimationFrame(animate);
      };

      /*
       * Keep the original navigation and sticky navigation synchronized.
       * Touch and pen input use the browser's native inertial scrolling.
       */
      nav.addEventListener(
        'scroll',
        () => {
          syncMirrorNavigation();
        },
        { passive: true }
      );

      nav.addEventListener('pointerdown', (event) => {
        /*
         * Let touch and pen use native horizontal scrolling.
         * Custom drag-to-scroll is only used for a desktop mouse.
         */
        if (event.pointerType !== 'mouse') {
          return;
        }

        if (event.button !== 0) {
          return;
        }

        stopMomentum();

        state.isPointerDown = true;
        state.isDragging = false;
        state.pointerId = event.pointerId;
        state.startX = event.clientX;
        state.startScrollLeft = nav.scrollLeft;
        state.lastX = event.clientX;
        state.lastTime = performance.now();
        state.velocity = 0;

        nav.classList.add('is-pointer-down');
      });

      nav.addEventListener('pointermove', (event) => {
        if (
          !state.isPointerDown ||
          event.pointerId !== state.pointerId
        ) {
          return;
        }

        const movement = event.clientX - state.startX;

        if (
          !state.isDragging &&
          Math.abs(movement) > 5
        ) {
          state.isDragging = true;

          if (
            nav.setPointerCapture &&
            !nav.hasPointerCapture?.(event.pointerId)
          ) {
            nav.setPointerCapture(event.pointerId);
          }

          nav.classList.add('is-dragging');
        }

        if (!state.isDragging) return;

        event.preventDefault();

        const now = performance.now();
        const elapsed = Math.max(1, now - state.lastTime);

        /*
         * Positive velocity means the content continues scrolling right.
         */
        const instantVelocity =
          (state.lastX - event.clientX) / elapsed;

        state.velocity =
          state.velocity * 0.65 +
          instantVelocity * 0.35;

        state.lastX = event.clientX;
        state.lastTime = now;

        setScrollLeft(
          state.startScrollLeft - movement
        );
      });

      const finishDrag = (event) => {
        if (
          !state.isPointerDown ||
          event.pointerId !== state.pointerId
        ) {
          return;
        }

        state.isPointerDown = false;

        if (
          nav.releasePointerCapture &&
          nav.hasPointerCapture?.(event.pointerId)
        ) {
          nav.releasePointerCapture(event.pointerId);
        }

        nav.classList.remove('is-pointer-down');

        if (state.isDragging) {
          state.suppressClickUntil =
            performance.now() + 150;

          startMomentum();
        } else {
          syncMirrorNavigation();
        }

        state.isDragging = false;
        state.pointerId = null;

        nav.classList.remove('is-dragging');
      };

      nav.addEventListener('pointerup', finishDrag);
      nav.addEventListener('pointercancel', finishDrag);

      nav.addEventListener('lostpointercapture', () => {
        if (!state.isPointerDown) return;

        state.isPointerDown = false;
        state.isDragging = false;
        state.pointerId = null;

        nav.classList.remove(
          'is-pointer-down',
          'is-dragging'
        );

        syncMirrorNavigation();
      });

      nav.addEventListener(
        'click',
        (event) => {
          const collectionCard = event.target.closest(
            'a.fct__collection-card[href]'
          );

          if (
            !collectionCard ||
            !nav.contains(collectionCard)
          ) {
            return;
          }

          if (
            performance.now() <
            state.suppressClickUntil
          ) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            collectionCard.target === '_blank'
          ) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          window.location.assign(collectionCard.href);
        },
        true
      );

      nav.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });
    }

    centerActiveCollection(nav) {
      const active = nav?.querySelector('.is-active');

      if (!active || !nav) return;

      window.requestAnimationFrame(() => {
        const left =
          active.offsetLeft -
          (nav.clientWidth - active.clientWidth) / 2;

        nav.scrollTo({
          left: Math.max(0, left),
          behavior: 'auto',
        });
      });
    }

    scrollNavigation(direction, nav) {
      if (!nav) return;

      const firstCard = nav.querySelector('.fct__collection-card');
      const navStyles = window.getComputedStyle(nav);
      const gap =
        Number.parseFloat(navStyles.columnGap || navStyles.gap) || 0;

      const amount = firstCard
        ? firstCard.getBoundingClientRect().width + gap
        : nav.clientWidth * 0.7;

      nav.scrollBy({
        left: direction * amount,
        behavior: 'smooth',
      });
    }

    applySort(value) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      window.location.assign(url.toString());
    }

    openDrawer(trigger) {
      this.openNativeFacetDrawer(trigger);
    }

    closeDrawer() {
      // SLEEK's native FacetDrawer owns its own close behavior.
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      const openDropdown = this.querySelector(
        '[data-fct-sort-dropdown].is-open'
      );

      if (!openDropdown) return;

      event.preventDefault();
      openDropdown.__fctSetExpanded?.(false);
      openDropdown.querySelector('[data-fct-sort-trigger]')?.focus();
    }
  }

  customElements.define(
    'fragrance-collection-toolbar',
    FragranceCollectionToolbar
  );
}